// === Веб-интеграция соц-логина (Google OAuth implicit + Facebook SDK) + deviceId гостя ===
//
// Гейт по env (как сервер гейтит по своим ключам):
//   REACT_APP_GOOGLE_CLIENT_ID   — включает Google
//   REACT_APP_FACEBOOK_APP_ID    — включает Facebook
// Apple на вебе пока выключен — на сервере нет эндпоинта /api/auth/apple.
//
// Google: НАША кнопка по клику открывает popup (google.accounts.oauth2 implicit-флоу) и
// отдаёт access_token. Сервер проверяет его (aud === наш client_id) и тянет профиль.
// Так кнопка остаётся в нашем дизайне (не персонализированный белый виджет Google).

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';

export const socialConfig = {
  google: !!GOOGLE_CLIENT_ID,
  facebook: !!FACEBOOK_APP_ID,
  apple: false, // нет серверного эндпоинта
};

// Стабильный deviceId установки для гостевого входа.
export const getDeviceId = () => {
  const KEY = 'blotme_device_id';
  let id = '';
  try { id = localStorage.getItem(KEY) || ''; } catch {}
  if (!id || id.length < 8) {
    const rnd = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    id = `web-${rnd}`;
    try { localStorage.setItem(KEY, id); } catch {}
  }
  return id;
};

// Ленивая загрузка внешнего скрипта (один раз на src).
const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) return resolve();
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  s.defer = true;
  s.onload = () => resolve();
  s.onerror = () => reject(new Error(`Не удалось загрузить ${src}`));
  document.head.appendChild(s);
});

// Ждём, пока gsi/client доинициализирует google.accounts (после onload бывает не сразу).
const waitForGoogle = (tries = 25) => new Promise((resolve, reject) => {
  const check = (n) => {
    const g = window.google;
    if (g && g.accounts && g.accounts.oauth2) return resolve(g);
    if (n <= 0) return reject(new Error('Google SDK не загрузился'));
    setTimeout(() => check(n - 1), 100);
  };
  check(tries);
});

// === Google: implicit token client ===
// Инициализируем заранее (на маунте логина), чтобы по клику открыть popup СИНХРОННО
// внутри жеста пользователя — иначе браузер заблокирует всплывающее окно.
let _tokenClient = null;
let _pending = null; // { resolve, reject } текущего запроса

export const initGoogle = () => {
  if (!GOOGLE_CLIENT_ID) return Promise.reject(new Error('Google не настроен'));
  if (_tokenClient) return Promise.resolve();
  return loadScript('https://accounts.google.com/gsi/client')
    .then(() => waitForGoogle())
    .then((google) => {
      if (_tokenClient) return;
      _tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        callback: (resp) => {
          const cb = _pending; _pending = null;
          if (!cb) return;
          if (resp && resp.access_token) cb.resolve(resp.access_token);
          else cb.reject(new Error(resp && resp.error_description ? resp.error_description : 'Вход через Google отменён'));
        },
        error_callback: (err) => {
          const cb = _pending; _pending = null;
          if (cb) cb.reject(new Error(err && err.message ? err.message : 'Вход через Google отменён'));
        },
      });
    });
};

// Открыть popup Google и получить access_token. ВЫЗЫВАТЬ ИЗ onClick (синхронно в жесте).
export const requestGoogleToken = () => new Promise((resolve, reject) => {
  if (!_tokenClient) return reject(new Error('Google ещё не готов — попробуй ещё раз через секунду'));
  _pending = { resolve, reject };
  _tokenClient.requestAccessToken();
});

// === Facebook → accessToken (его проверяет сервер /api/auth/facebook) ===
let fbInited = false;
export const getFacebookAccessToken = () => new Promise((resolve, reject) => {
  if (!FACEBOOK_APP_ID) return reject(new Error('Facebook не настроен'));
  loadScript('https://connect.facebook.net/en_US/sdk.js').then(() => {
    const FB = window.FB;
    if (!FB) return reject(new Error('Facebook SDK недоступен'));
    if (!fbInited) {
      FB.init({ appId: FACEBOOK_APP_ID, cookie: true, xfbml: false, version: 'v19.0' });
      fbInited = true;
    }
    FB.login((resp) => {
      if (resp && resp.authResponse && resp.authResponse.accessToken) {
        resolve(resp.authResponse.accessToken);
      } else {
        reject(new Error('Вход через Facebook отменён'));
      }
    }, { scope: 'email,public_profile' });
  }).catch(reject);
});
